/**
 * Sistema de logging condicional baseado no ambiente
 * Logs só são exibidos em desenvolvimento
 */

const isDevelopment = process.env.NODE_ENV === 'development';

/**
 * Logger principal com diferentes níveis de log
 */
export const logger = {
  /**
   * Log de informação (azul)
   * @param {string} message - Mensagem principal
   * @param {any} data - Dados adicionais para log
   */
  info: (message, data = null) => {
    if (!isDevelopment) return;
    
    console.log(
      `%cℹ️ [INFO] ${message}`,
      'color: #2196F3; font-weight: bold; font-size: 12px;',
      data || ''
    );
  },

  /**
   * Log de sucesso (verde)
   * @param {string} message - Mensagem principal
   * @param {any} data - Dados adicionais para log
   */
  success: (message, data = null) => {
    if (!isDevelopment) return;
    
    console.log(
      `%c✅ [SUCCESS] ${message}`,
      'color: #4CAF50; font-weight: bold; font-size: 12px;',
      data || ''
    );
  },

  /**
   * Log de aviso (amarelo)
   * @param {string} message - Mensagem principal
   * @param {any} data - Dados adicionais para log
   */
  warn: (message, data = null) => {
    if (!isDevelopment) return;
    
    console.warn(
      `%c⚠️ [WARN] ${message}`,
      'color: #FF9800; font-weight: bold; font-size: 12px;',
      data || ''
    );
  },

  /**
   * Log de erro (vermelho)
   * @param {string} message - Mensagem principal
   * @param {any} data - Dados adicionais para log
   */
  error: (message, data = null) => {
    // Erros sempre são logados, mesmo em produção
    console.error(
      `%c❌ [ERROR] ${message}`,
      'color: #F44336; font-weight: bold; font-size: 12px;',
      data || ''
    );
  },

  /**
   * Log de requisição API (azul claro)
   * @param {string} method - Método HTTP
   * @param {string} endpoint - Endpoint da API
   * @param {any} data - Dados da requisição
   */
  apiRequest: (method, endpoint, data = null) => {
    if (!isDevelopment) return;
    
    const timestamp = new Date().toLocaleTimeString('pt-BR');
    console.log(
      `%c🚀 [API REQUEST] ${method?.toUpperCase()} ${endpoint}`,
      'color: #2196F3; font-weight: bold; font-size: 12px;',
      {
        '📅 Hora': timestamp,
        '📤 Dados Enviados': data,
        '🛣️ Endpoint': endpoint
      }
    );
  },

  /**
   * Log de resposta API (verde)
   * @param {number} status - Status HTTP
   * @param {string} endpoint - Endpoint da API
   * @param {any} data - Dados da resposta
   */
  apiResponse: (status, endpoint, data = null) => {
    if (!isDevelopment) return;
    
    const timestamp = new Date().toLocaleTimeString('pt-BR');
    console.log(
      `%c✅ [API RESPONSE] ${status} - ${endpoint}`,
      'color: #4CAF50; font-weight: bold; font-size: 12px;',
      {
        '📅 Hora': timestamp,
        '✅ Status': status,
        '📥 Dados Recebidos': data,
        '🛣️ Endpoint': endpoint
      }
    );
  },

  /**
   * Log de erro API (vermelho)
   * @param {string} method - Método HTTP
   * @param {string} endpoint - Endpoint da API
   * @param {any} error - Erro ocorrido
   */
  apiError: (method, endpoint, error = null) => {
    // Erros de API sempre são logados
    const timestamp = new Date().toLocaleTimeString('pt-BR');
    console.error(
      `%c❌ [API ERROR] ${method?.toUpperCase()} ${endpoint}`,
      'color: #F44336; font-weight: bold; font-size: 12px;',
      {
        '📅 Hora': timestamp,
        '❌ Erro': error?.message || error,
        '🔢 Código': error?.code || 'N/A',
        '📊 Status HTTP': error?.response?.status || 'N/A',
        '🛣️ Endpoint': endpoint
      }
    );
  },

  /**
   * Log de tabela (para dados estruturados)
   * @param {string} title - Título da tabela
   * @param {Object} data - Dados para exibir em tabela
   */
  table: (title, data) => {
    if (!isDevelopment) return;
    
    console.log(`%c📊 [TABLE] ${title}`, 'color: #9C27B0; font-weight: bold; font-size: 12px;');
    console.table(data);
  },

  /**
   * Log de grupo (para agrupar logs relacionados)
   * @param {string} label - Label do grupo
   * @param {Function} callback - Função que contém os logs do grupo
   */
  group: (label, callback) => {
    if (!isDevelopment) {
      callback();
      return;
    }
    
    console.group(`%c📦 [GROUP] ${label}`, 'color: #607D8B; font-weight: bold; font-size: 12px;');
    callback();
    console.groupEnd();
  },

  /**
   * Log de depuração (apenas em desenvolvimento)
   * @param {string} message - Mensagem de debug
   * @param {any} data - Dados adicionais
   */
  debug: (message, data = null) => {
    if (!isDevelopment) return;
    
    console.log(
      `%c🐛 [DEBUG] ${message}`,
      'color: #9E9E9E; font-weight: bold; font-size: 11px; font-style: italic;',
      data || ''
    );
  }
};

/**
 * Função auxiliar para verificar se está em desenvolvimento
 */
export const isDev = () => isDevelopment;

/**
 * Função auxiliar para verificar se está em produção
 */
export const isProd = () => !isDevelopment;

export default logger;

